import { useEffect, useRef, useState } from 'react';

import { getExecution } from '@/shared/api/execution';
import type { ExecutionStatus } from '@/shared/api/execution';

import { prefixWorkbenchLogBlock } from '@/features/execution/lib/prefixWorkbenchLogLine';
import { translateExecutionWorkbenchLogLine } from '@/features/execution/lib/translateExecutionWorkbenchLogLine';

const LOG_LINE_EVENT_NAMES = ['log', 'line', 'stdout', 'stderr', 'chunk', 'text', 'data'] as const;
const MAX_LOG_SIZE = 100_000;
const POLLING_INTERVAL_QUEUED_MS = 3000;
const POLLING_INTERVAL_RUNNING_MS = 5000;
const IS_DEMO_MODE = import.meta.env.MODE === 'demo';

const TERMINAL_EXECUTION_STATUSES: ExecutionStatus[] = [
  'execution_succeeded',
  'execution_failed',
  'execution_canceled',
];

type UseExecutionLogsOptions = {
  onComplete?: (status: ExecutionStatus) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: ExecutionStatus) => void;
  enabled?: boolean;
};

export const useExecutionLogs = (
  runId: number | null,
  executionId: number | null,
  options: UseExecutionLogsOptions = {}
) => {
  const { onComplete, onError, onStatusChange, enabled = true } = options;

  const [logs, setLogs] = useState<string>('');
  const [isConnected, setIsConnected] = useState(false);
  const [isTerminated, setIsTerminated] = useState(false);

  const logEsRef = useRef<EventSource | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const onStatusChangeRef = useRef(onStatusChange);
  const enabledRef = useRef(enabled);
  const hasCompletedRef = useRef(false);
  const currentStatusRef = useRef<ExecutionStatus | null>(null);
  const demoLogStatusRef = useRef<ExecutionStatus | null>(null);

  useEffect(() => {
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
    onStatusChangeRef.current = onStatusChange;
    enabledRef.current = enabled;
  }, [onComplete, onError, onStatusChange, enabled]);

  useEffect(() => {
    if (executionId == null) {
      setIsConnected(false);
      setIsTerminated(false);
      setLogs('');
      hasCompletedRef.current = false;
      currentStatusRef.current = null;
      demoLogStatusRef.current = null;
    }
  }, [executionId]);

  useEffect(() => {
    if (!runId || !executionId || !enabled || isTerminated || hasCompletedRef.current) {
      return;
    }

    const appendLogLine = (raw: string) => {
      const text = String(raw ?? '').trim();
      if (!text) return;
      const localized = translateExecutionWorkbenchLogLine(text);
      const stamped = prefixWorkbenchLogBlock(localized);
      setLogs((prev) => {
        const line = stamped.endsWith('\n') ? stamped : `${stamped}\n`;
        const newLogs = prev + line;

        if (newLogs.length > MAX_LOG_SIZE) {
          const trimmed = newLogs.slice(-MAX_LOG_SIZE);
          const firstNewline = trimmed.indexOf('\n');
          return firstNewline >= 0 ? trimmed.slice(firstNewline + 1) : trimmed;
        }

        return newLogs;
      });
    };

    const reportStatusChange = (status: ExecutionStatus) => {
      if (currentStatusRef.current === status) return;
      currentStatusRef.current = status;
      onStatusChangeRef.current?.(status);
    };

    const appendDemoStatusLog = (execution: Awaited<ReturnType<typeof getExecution>>) => {
      if (!IS_DEMO_MODE || demoLogStatusRef.current === execution.status) return;

      demoLogStatusRef.current = execution.status;

      switch (execution.status) {
        case 'execution_queued':
          appendLogLine('실행 요청이 접수되었습니다. 리소스를 기다리는 중...');
          break;
        case 'execution_running':
          appendLogLine('실행을 시작했습니다. 데이터셋, 피처셋 및 파라미터셋을 불러오며 모델을 학습 중입니다...');
          break;
        case 'execution_succeeded':
          appendLogLine('실행이 성공적으로 완료되었습니다. 메트릭과 산출물을 확인할 수 있습니다.');
          break;
        case 'execution_canceled':
          appendLogLine('사용자에 의해 실행이 취소되었습니다.');
          break;
        case 'execution_failed':
          appendLogLine(execution.errorMessage || '실행 중 오류가 발생했습니다.');
          break;
      }
    };

    const handleCompletion = (status: ExecutionStatus, errorMessage?: string) => {
      if (hasCompletedRef.current) return;
      hasCompletedRef.current = true;

      if (status === 'execution_failed') {
        if (errorMessage) {
          appendLogLine(errorMessage);
        }
        appendLogLine('실행에 실패했습니다.');
      }

      setIsTerminated(true);
      setIsConnected(false);

      if (logEsRef.current) {
        logEsRef.current.close();
        logEsRef.current = null;
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      reportStatusChange(status);
      onCompleteRef.current?.(status);
    };

    const checkExecutionStatus = async () => {
      if (!enabledRef.current || hasCompletedRef.current) return;

      try {
        const execution = await getExecution(runId, executionId);
        const status = execution.status;

        appendDemoStatusLog(execution);
        reportStatusChange(status);

        if (status === 'execution_running') {
          if (!IS_DEMO_MODE) {
            startLogStream();
          }

          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = setInterval(checkExecutionStatus, POLLING_INTERVAL_RUNNING_MS);
          }
        } else if (TERMINAL_EXECUTION_STATUSES.includes(status)) {
          handleCompletion(status, execution.errorMessage || undefined);
        }
      } catch (error) {
        console.error('[useExecutionLogs] 상태 폴링 실패:', error);
        onErrorRef.current?.(error as Error);
      }
    };

    const startLogStream = () => {
      if (IS_DEMO_MODE) return;
      if (logEsRef.current || hasCompletedRef.current) return;

      const url = `/backend/api/v1/runs/${runId}/executions/${executionId}/logs/stream`;
      const eventSource = new EventSource(url, { withCredentials: true });
      logEsRef.current = eventSource;

      eventSource.onopen = () => {
        setIsConnected(true);
      };

      eventSource.onmessage = (event) => {
        appendLogLine(event.data);
      };

      for (const name of LOG_LINE_EVENT_NAMES) {
        eventSource.addEventListener(name, (event: MessageEvent) => {
          appendLogLine(String((event as MessageEvent<string>).data ?? ''));
        });
      }

      eventSource.addEventListener('status', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent).data) as {
            status?: ExecutionStatus;
            message?: string;
          };
          const status = data.status;
          if (!status) return;

          reportStatusChange(status);

          if (TERMINAL_EXECUTION_STATUSES.includes(status)) {
            if (data.message) {
              appendLogLine(data.message);
            }
            handleCompletion(status);
          }
        } catch (error) {
          console.error('[useExecutionLogs] status 이벤트 파싱 실패:', error);
        }
      });

      eventSource.addEventListener('complete', async () => {
        if (hasCompletedRef.current) return;

        setIsConnected(false);
        if (logEsRef.current) {
          logEsRef.current.close();
          logEsRef.current = null;
        }

        try {
          const execution = await getExecution(runId, executionId);
          if (TERMINAL_EXECUTION_STATUSES.includes(execution.status)) {
            handleCompletion(execution.status, execution.errorMessage || undefined);
          }
        } catch (error) {
          console.error('[useExecutionLogs] complete 이벤트 처리 중 상태 확인 실패:', error);

          if (currentStatusRef.current && TERMINAL_EXECUTION_STATUSES.includes(currentStatusRef.current)) {
            handleCompletion(currentStatusRef.current);
          } else {
            setIsTerminated(true);
            hasCompletedRef.current = true;
          }
        }
      });

      eventSource.onerror = async () => {
        setIsConnected(false);
        if (logEsRef.current) {
          logEsRef.current.close();
          logEsRef.current = null;
        }

        if (hasCompletedRef.current) return;

        try {
          const execution = await getExecution(runId, executionId);
          if (TERMINAL_EXECUTION_STATUSES.includes(execution.status)) {
            handleCompletion(execution.status, execution.errorMessage || undefined);
          }
        } catch (error) {
          console.error('[useExecutionLogs] SSE 에러 후 최종 상태 확인 실패:', error);
        }
      };
    };

    pollingIntervalRef.current = setInterval(checkExecutionStatus, POLLING_INTERVAL_QUEUED_MS);
    void checkExecutionStatus();

    return () => {
      if (logEsRef.current) {
        logEsRef.current.close();
        logEsRef.current = null;
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [runId, executionId, enabled, isTerminated]);

  return {
    logs,
    isConnected,
    isTerminated,
  };
};
